// Moon phase calculations for the Astrolabe sky view.
//
// The phase angle is the angle between the Sun and the Moon as seen from
// Earth: 0° = new moon, 90° = quarter, 180° = full moon.

export interface MoonPhase {
  /** Phase angle in degrees between Sun and Moon (0..180). */
  phase: number;
  /** Illuminated fraction of the Moon's disc (0..1). */
  illumination: number;
  /** True while the Moon is waxing (growing toward full). */
  waxing: boolean;
  /**
   * Earthshine strength on the night side (0..1). Earthshine is sunlight
   * reflected off the Earth onto the Moon, so it is strongest at new moon
   * (the Earth is "full" as seen from the Moon) and absent at full moon.
   */
  earthshine: number;
}

/** Phase of the Moon from the ecliptic longitudes of the Sun and the Moon. */
export const getMoonPhase = (sunLon: number, moonLon: number): MoonPhase => {
  const diff = Math.abs(sunLon - moonLon) % 360;
  const phase = Math.min(diff, 360 - diff);
  const cosPhase = Math.cos((phase * Math.PI) / 180);
  // The Moon waxes while it is east of the Sun (its longitude leads the
  // Sun's). The new- and full-moon boundaries count as not waxing.
  const moonEast = (((moonLon - sunLon) % 360) + 360) % 360;
  const waxing = moonEast > 0 && moonEast < 180;
  return {
    phase,
    illumination: (1 - cosPhase) / 2,
    waxing,
    earthshine: (1 + cosPhase) / 2,
  };
};

// Moon visibility near the Sun (angular separation in degrees), matching how
// the real Moon behaves:
// - 0–5°: invisible — lost in the Sun's glare (≈ the Danjon limit, the
//   thinnest crescent the eye can detect).
// - 5–18°: an extremely thin crescent emerges; its lit area grows roughly
//   quadratically with elongation, so the opacity ramps quadratically up to
//   a faint "almost transparent" level at 18°.
// - 18–60°: linear growth to fully visible at 60°.
const MOON_INVISIBLE_DEG = 5;
const MOON_EMERGE_DEG = 18;
const MOON_FULL_VISIBLE_DEG = 60;
const EMERGE_PEAK_OPACITY = 0.12;

/**
 * Opacity (0..1) of the Moon and its glow for a given Sun–Moon angular
 * separation, so the Moon melts away as it approaches a conjunction.
 */
export const moonVisibilityOpacity = (sepDeg: number): number => {
  if (sepDeg <= MOON_INVISIBLE_DEG) return 0;
  if (sepDeg < MOON_EMERGE_DEG) {
    const t =
      (sepDeg - MOON_INVISIBLE_DEG) / (MOON_EMERGE_DEG - MOON_INVISIBLE_DEG);
    return EMERGE_PEAK_OPACITY * t * t;
  }
  if (sepDeg < MOON_FULL_VISIBLE_DEG) {
    const t =
      (sepDeg - MOON_EMERGE_DEG) / (MOON_FULL_VISIBLE_DEG - MOON_EMERGE_DEG);
    return EMERGE_PEAK_OPACITY + (1 - EMERGE_PEAK_OPACITY) * t;
  }
  return 1;
};

// Extra Moon transparency during daylight (0..1 added opacity). The Moon is
// hard to spot in a bright sky, so once the Sun clears the same ~18° band the
// horizon uses for sunrise/sunset the Moon keeps a fixed extra fade — ramped
// in linearly while the Sun rises through that band and removed linearly
// while it sets.
const DAYLIGHT_MOON_FADE = 0.35;
const SUN_RAMP_DEG = 18;

/**
 * Additional Moon transparency caused by daylight: 0 at night, ramping to
 * `DAYLIGHT_MOON_FADE` once the Sun is ≥18° above the horizon.
 */
export const moonDaylightFade = (
  sunAboveHorizon: boolean,
  sunAltDeg: number,
): number =>
  sunAboveHorizon
    ? DAYLIGHT_MOON_FADE * Math.min(1, Math.max(0, sunAltDeg) / SUN_RAMP_DEG)
    : 0;

export interface MoonLitPath {
  /** SVG path `d` of the Sun-lit region of the Moon. */
  d: string;
  /** Fill rule required to render the path correctly. */
  fillRule: "nonzero" | "evenodd";
}

/**
 * Build the SVG path of the Moon's Sun-lit region for a disc of radius `R`.
 *
 * The Moon's local +x axis points toward the Sun, so the lit side is +x:
 * - Crescent (phase <= 90°): bounded by the limb arc on the Sun side and
 *   the terminator ellipse (semi-axes R·cos(phase) toward the Sun, R across).
 * - Gibbous (phase > 90°): the full disc minus the dark crescent, punched
 *   out with the even-odd fill rule.
 *
 * The night side (with its earthshine tint) is drawn separately as a full
 * disc underneath this path.
 */
export const moonLitPath = (R: number, phase: number): MoonLitPath => {
  // Round to 4 decimals so the emitted SVG stays tidy despite
  // floating-point noise (e.g. cos(90°) ≈ 6.1e-16).
  const r4 = (n: number) => Math.round(n * 10000) / 10000;
  const cosPhase = Math.cos((phase * Math.PI) / 180);
  const Rr = r4(R);
  if (phase <= 90) {
    const termX = r4(R * cosPhase);
    return {
      d: `M 0,${-Rr} A ${Rr},${Rr} 0 0 1 0,${Rr} A ${termX},${Rr} 0 0 0 0,${-Rr} Z`,
      fillRule: "nonzero",
    };
  }
  const termX = r4(R * Math.abs(cosPhase));
  const fullDisc = `M 0,${-Rr} A ${Rr},${Rr} 0 1 1 0,${Rr} A ${Rr},${Rr} 0 1 1 0,${-Rr}`;
  const darkCrescent = `M 0,${-Rr} A ${Rr},${Rr} 0 0 0 0,${Rr} A ${termX},${Rr} 0 0 1 0,${-Rr} Z`;
  return { d: `${fullDisc} ${darkCrescent}`, fillRule: "evenodd" };
};
